import { ApiError } from '@swarm/api-client';
import { Alert, Button, List, ListRow, Section, Spinner, Stack, Switch, Text } from '@ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useMeta } from '../../system/useMeta';
import { useAuthorizationPrompt, useDecideAuthorization, type AuthorizationParams } from '../api';
import { PublicPage } from '../components/PublicPage';
import { useSession } from '../session';

const PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'scope',
  'state',
  'code_challenge',
  'code_challenge_method',
  'resource',
] as const;

/** Sends the browser back to the MCP client. */
function leave(url: string) {
  window.location.assign(url);
}

/**
 * OAuth authorization endpoint (`/oauth/authorize`, MCP-003): an MCP client
 * such as Claude asks for access; the signed-in member allows or denies.
 * Writing can be switched off here (only reading then).
 */
export function AuthorizePage() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const { me } = useSession();
  const meta = useMeta();
  const params = useMemo(() => {
    const p: AuthorizationParams = {};
    for (const key of PARAMS) {
      const value = search.get(key);
      if (value !== null) p[key] = value;
    }
    return p;
  }, [search]);
  const prompt = useAuthorizationPrompt(params);
  const decide = useDecideAuthorization();
  const [write, setWrite] = useState(true);

  // Errors the client must hear about go back to it right away.
  const redirectTo =
    prompt.error instanceof ApiError && prompt.error.status === 422
      ? prompt.error.params.redirect_to
      : undefined;
  useEffect(() => {
    if (redirectTo) leave(redirectTo);
  }, [redirectTo]);

  const submit = (approve: boolean) =>
    decide.mutate(
      {
        ...params,
        approve,
        scopes: write ? ['read', 'write'] : ['read'],
      },
      { onSuccess: leave },
    );

  if (prompt.isPending || redirectTo) {
    return (
      <PublicPage title={t('oauth.title')}>
        <Spinner label={t('common.loading')} />
      </PublicPage>
    );
  }
  if (prompt.error || !prompt.data) {
    return (
      <PublicPage title={t('oauth.invalidTitle')}>
        <Alert tone="danger">{errorMessage(prompt.error)}</Alert>
        <Text tone="muted">{t('oauth.invalid')}</Text>
      </PublicPage>
    );
  }
  const p = prompt.data;
  const asksWrite = p.scopes.includes('write');
  return (
    <PublicPage title={t('oauth.title')}>
      <Stack gap={4}>
        <Text>
          {t('oauth.request', {
            client: p.client_name,
            business: meta.data?.business_name ?? 'Imkerei',
          })}
        </Text>
        <Section>
          <List>
            <ListRow title={t('oauth.read')} description={t('oauth.readText')} />
            {asksWrite && (
              <ListRow
                title={t('oauth.write')}
                description={t('oauth.writeText')}
                trailing={
                  <Switch
                    aria-label={t('oauth.write')}
                    checked={write}
                    onChange={(e) => setWrite(e.target.checked)}
                  />
                }
              />
            )}
          </List>
        </Section>
        <Text size="sm" tone="muted">
          {t('oauth.account', { name: me.name })} · {t('oauth.redirect', { host: p.redirect_host })}
        </Text>
        {decide.error && <Alert tone="danger">{errorMessage(decide.error)}</Alert>}
        <Stack gap={2}>
          <Button size="lg" fullWidth loading={decide.isPending} onClick={() => submit(true)}>
            {t('oauth.allow')}
          </Button>
          <Button
            size="lg"
            variant="secondary"
            fullWidth
            disabled={decide.isPending}
            onClick={() => submit(false)}
          >
            {t('oauth.deny')}
          </Button>
        </Stack>
      </Stack>
    </PublicPage>
  );
}
